// src/Views/UserManagementView.jsx
import React, { useState, useEffect } from 'react';
import PageHeader from '../components/Common/PageHeader';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

const UserManagementView = ({ mockData, setMockData, showNotification }) => {
  const { isAuthenticated, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [userPractitionerAssociations, setUserPractitionerAssociations] = useState([]);
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);

  // API related state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Fetch users from API
  const fetchUsers = async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const searchParams = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        ...params
      };

      if (searchTerm) {
        searchParams.search = searchTerm;
      }

      if (filterStatus !== 'all') {
        searchParams.status = filterStatus;
      }

      const response = await apiService.getUsers(searchParams);

      if (response.data) {
        // Transform API data to match expected format
        const transformedUsers = response.data.map(user => ({
          id: user.id,
          name: user.fullName,
          email: user.email,
          role: user.role || 'Receptionist',
          avatar: user.firstName && user.lastName ?
            `${user.firstName[0]}${user.lastName[0]}` : 'U',
          clinics: [{
            id: user.company?.id || 'unknown',
            name: user.company?.name || 'Unknown Company',
            role: user.role || 'Receptionist'
          }],
          lastActive: user.lastLogin ?
            new Date(user.lastLogin).toLocaleDateString() : 'Never',
          status: user.status?.toLowerCase() || 'active',
          joinDate: new Date(user.createdAt).toLocaleDateString(),
          permissions: [], // To be implemented
          phone: user.phone || '',
          department: 'General',
          firstName: user.firstName,
          lastName: user.lastName,
          emailVerified: user.emailVerified,
          twoFactorEnabled: user.twoFactorEnabled,
          language: user.language,
          timezone: user.timezone,
          calendarIntegration: {
            googleCalendar: { enabled: false, email: '' },
            outlookCalendar: { enabled: false, email: '' },
            timezone: user.timezone || 'Europe/London',
            syncBookings: false
          }
        }));

        setUsers(transformedUsers);
        setPagination({
          page: response.page,
          pageSize: response.pageSize,
          totalCount: response.totalCount,
          totalPages: response.totalPages
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users. Please try again.');
      showNotification('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch practitioners from API
  const fetchPractitioners = async () => {
    try {
      const response = await apiService.getPractitioners();

      if (response && Array.isArray(response)) {
        // Transform API data to match expected format
        const transformedPractitioners = response.map(practitioner => ({
          id: practitioner.id,
          name: practitioner.name || `${practitioner.firstName} ${practitioner.lastName}`.trim(),
          specialization: practitioner.specialization || 'General Practice',
          status: practitioner.status?.toLowerCase() || 'active'
        }));
        setPractitioners(transformedPractitioners);
      } else if (response.Data && Array.isArray(response.Data)) {
        // Handle paginated response
        const transformedPractitioners = response.Data.map(practitioner => ({
          id: practitioner.id,
          name: practitioner.name || `${practitioner.firstName} ${practitioner.lastName}`.trim(),
          specialization: practitioner.specialization || 'General Practice',
          status: practitioner.status?.toLowerCase() || 'active'
        }));
        setPractitioners(transformedPractitioners);
      }
    } catch (error) {
      console.error('Error fetching practitioners:', error);
      // Fallback to mock data if API fails
      const fallbackPractitioners = [
        { id: 'PRAC001', name: 'Dr. Sarah Wilson', specialization: 'Physiotherapy', status: 'active' },
        { id: 'PRAC002', name: 'Dr. James Smith', specialization: 'Sports Massage', status: 'active' },
        { id: 'PRAC003', name: 'Dr. Priya Patel', specialization: 'Osteopathy', status: 'active' },
        { id: 'PRAC004', name: 'Dr. Michael Chen', specialization: 'Acupuncture', status: 'inactive' }
      ];
      setPractitioners(fallbackPractitioners);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
      fetchPractitioners();
    } else {
      setLoading(false);
      setError('Please log in to view users');
    }

    // Initialize User-Practitioner Associations
    const initialAssociations = [
      {
        id: 'assoc-001',
        userId: 'user-001',
        practitionerId: 'PRAC001',
        role: 'manager',
        permissions: ['view', 'edit', 'book', 'cancel', 'reschedule'],
        clinicId: 'harley-street',
        createdDate: '2024-01-15'
      },
      {
        id: 'assoc-002',
        userId: 'user-001',
        practitionerId: 'PRAC002',
        role: 'manager',
        permissions: ['view', 'edit', 'book', 'cancel', 'reschedule'],
        clinicId: 'harley-street',
        createdDate: '2024-01-15'
      },
      {
        id: 'assoc-003',
        userId: 'user-002',
        practitionerId: 'PRAC001',
        role: 'assistant',
        permissions: ['view', 'book', 'cancel'],
        clinicId: 'harley-street',
        createdDate: '2024-02-20'
      },
      {
        id: 'assoc-004',
        userId: 'user-002',
        practitionerId: 'PRAC002',
        role: 'assistant',
        permissions: ['view', 'book'],
        clinicId: 'harley-street',
        createdDate: '2024-02-20'
      }
    ];
    setUserPractitionerAssociations(initialAssociations);
  }, [isAuthenticated]);

  // Refetch users when filters change
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    }
  }, [searchTerm, filterStatus, pagination.page, isAuthenticated]);

  const availableClinics = [
    { id: 'harley-street', name: 'Harley Street Practice' },
    { id: 'canary-wharf', name: 'Canary Wharf Clinic' },
    { id: 'kensington', name: 'Kensington Wellness Hub' },
    { id: 'greenwich', name: 'Greenwich Community Clinic' }
  ];

  const rolePermissions = {
    'Practice Manager': {
      description: 'Complete oversight of practice operations and all practitioners',
      defaultPermissions: ['full-access', 'user-management', 'system-settings', 'practitioner-management'],
      practitionerPermissions: ['view', 'edit', 'book', 'cancel', 'reschedule', 'manage-schedule']
    },
    'Clinic Coordinator': {
      description: 'Manages scheduling and coordination across practitioner teams',
      defaultPermissions: ['booking-management', 'practitioner-coordination', 'schedule-management'],
      practitionerPermissions: ['view', 'book', 'cancel', 'reschedule']
    },
    'Receptionist': {
      description: 'Front desk operations with booking access for assigned practitioners',
      defaultPermissions: ['booking-management', 'customer-service', 'practitioner-booking'],
      practitionerPermissions: ['view', 'book', 'cancel']
    },
    'Admin Assistant': {
      description: 'Administrative support with limited practitioner access',
      defaultPermissions: ['booking-management', 'customer-service', 'practitioner-view'],
      practitionerPermissions: ['view', 'book']
    },
    'Practitioner': {
      description: 'Healthcare provider managing own schedule and patient records',
      defaultPermissions: ['own-schedule', 'patient-records', 'booking-management'],
      practitionerPermissions: ['view-own']
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.role.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesRole && matchesStatus && matchesSearch;
  });

  const roles = ['all', ...Object.keys(rolePermissions)];
  const statuses = ['all', 'active', 'inactive'];

  // Get associated practitioners for a user
  const getUserPractitioners = (userId) => {
    return userPractitionerAssociations
      .filter(assoc => assoc.userId === userId)
      .map(assoc => {
        const practitioner = practitioners.find(p => p.id === assoc.practitionerId);
        return { ...practitioner, association: assoc };
      })
      .filter(p => p.id); // Filter out any missing practitioners
  };

  // Slideout handlers
  const handleAddUser = () => {
    const formContent = (
      <UserForm 
        onSubmit={handleSubmitNewUser}
        onCancel={closeSlideout}
        availableClinics={availableClinics}
        rolePermissions={rolePermissions}
      />
    );
    openSlideout('Add New User', formContent, '600px');
  };

  const handleEditUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setEditingUser(user);

    const formContent = (
      <UserForm
        user={user}
        onSubmit={(formData) => handleSubmitEditUser(formData, userId)}
        onCancel={closeSlideout}
        availableClinics={availableClinics}
        rolePermissions={rolePermissions}
        isEdit={true}
      />
    );
    openSlideout('Edit User', formContent, '600px');
  };

  const handleViewUser = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    const userPractitioners = getUserPractitioners(userId);
    
    const detailContent = (
      <UserDetail 
        user={user}
        practitioners={userPractitioners}
        onEdit={() => handleEditUser(userId)}
        onManagePractitioners={() => handleManagePractitioners(userId)}
      />
    );
    openSlideout('User Details', detailContent, '700px');
  };

  const handleManagePractitioners = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    const userAssociations = userPractitionerAssociations.filter(assoc => assoc.userId === userId);
    
    const formContent = (
      <AssignPractitionersForm
        user={user}
        practitioners={practitioners}
        currentAssociations={userAssociations}
        availableClinics={availableClinics}
        rolePermissions={rolePermissions}
        onSubmit={(associations) => handleUpdatePractitionerAssociations(userId, associations)}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Manage Practitioners', formContent, '800px');
  };

  // CRUD operations
  const handleSubmitNewUser = async (formData) => {
    try {
      setLoading(true);

      // Prepare the create data for the API
      const createData = {
        firstName: formData.name.split(' ')[0] || '',
        lastName: formData.name.split(' ').slice(1).join(' ') || '',
        email: formData.email,
        phone: formData.phone || '',
        language: formData.language || 'en-GB',
        timezone: formData.calendarIntegration?.timezone || formData.timezone || 'Europe/London',
        role: formData.role
      };

      // Call the API to create the user
      await apiService.createUser(createData);

      // Refresh the users list to get the updated data
      await fetchUsers();

      closeSlideout();
      showNotification(`User ${formData.name} created successfully! ${formData.sendWelcome ? 'Welcome email sent.' : ''}`, 'success');
    } catch (error) {
      console.error('Error creating user:', error);
      showNotification('Failed to create user. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitEditUser = async (formData, userId) => {
    try {
      setLoading(true);

      if (!userId) {
        showNotification('Failed to update user: No user ID provided.', 'error');
        return;
      }

      // Prepare the update data for the API
      // Convert status to proper enum format (integer values)
      let statusEnum = null;
      if (formData.status) {
        // Map frontend status values to backend StatusType enum (integer values)
        const statusMapping = {
          'active': 0,    // StatusType.Active
          'inactive': 1,  // StatusType.Inactive
          'pending': 2,   // StatusType.Pending
          'suspended': 3, // StatusType.Suspended
          'draft': 4      // StatusType.Draft
        };
        statusEnum = statusMapping[formData.status.toLowerCase()];
        if (statusEnum === undefined) {
          statusEnum = 0; // Default to Active
        }
      }

      const updateData = {
        firstName: formData.name.split(' ')[0] || '',
        lastName: formData.name.split(' ').slice(1).join(' ') || '',
        phone: formData.phone,
        language: formData.language || 'en-GB',
        timezone: formData.calendarIntegration?.timezone || formData.timezone || 'Europe/London',
        status: statusEnum,
        role: formData.role
      };

      // Call the API to update the user
      await apiService.updateUser(userId, updateData);

      // Refresh the users list to get the updated data
      await fetchUsers();

      closeSlideout();
      setEditingUser(null);
      showNotification(`User ${formData.name} updated successfully!`, 'success');
    } catch (error) {
      console.error('Error updating user:', error);
      showNotification('Failed to update user. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePractitionerAssociations = (userId, newAssociations) => {
    // Remove existing associations for this user
    const filteredAssociations = userPractitionerAssociations.filter(assoc => assoc.userId !== userId);
    
    // Add new associations
    const updatedAssociations = [
      ...filteredAssociations,
      ...newAssociations.map((assoc, index) => ({
        id: `assoc-${Date.now()}-${index}`,
        userId: userId,
        practitionerId: assoc.practitionerId,
        role: assoc.role,
        permissions: assoc.permissions,
        clinicId: assoc.clinicId,
        createdDate: new Date().toISOString().split('T')[0]
      }))
    ];
    
    setUserPractitionerAssociations(updatedAssociations);
    closeSlideout();
    showNotification('Practitioner associations updated successfully!', 'success');
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);

      const userToDelete = users.find(u => u.id === userId);
      const userName = userToDelete?.name || 'User';

      // Call the API to delete the user
      await apiService.deleteUser(userId);

      // Refresh the users list to get the updated data
      await fetchUsers();

      closeSlideout();
      setEditingUser(null);
      showNotification(`User ${userName} deleted successfully.`, 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showNotification('Failed to delete user. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentForRole = (role) => {
    switch (role) {
      case 'Practice Manager':
      case 'Clinic Coordinator':
        return 'Management';
      case 'Admin Assistant':
        return 'Administration';
      case 'Practitioner':
        return 'Clinical';
      case 'Receptionist':
        return 'Front Desk';
      default:
        return 'General';
    }
  };

  const getRoleClass = (role) => {
    switch (role) {
      case 'Practice Manager': return 'role-manager';
      case 'Clinic Coordinator': return 'role-coordinator';
      case 'Admin Assistant': return 'role-assistant';
      case 'Practitioner': return 'role-practitioner';
      case 'Receptionist': return 'role-receptionist';
      default: return 'role-default';
    }
  };

  const getStatusClass = (status) => status === 'active' ? 'status-active' : 'status-cancelled';

  const renderUserCard = (user) => {
    const userPractitioners = getUserPractitioners(user.id);
    
    return (
      <div key={user.id} className="user-card">
        <div className="user-checkbox">
          <input 
            type="checkbox"
            checked={selectedUsers.includes(user.id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedUsers([...selectedUsers, user.id]);
              } else {
                setSelectedUsers(selectedUsers.filter(id => id !== user.id));
              }
            }}
          />
        </div>
        <div className="user-main">
          <div className="user-avatar-sm">{user.avatar}</div>
          <div className="user-info">
            <div className="user-name">{user.name}</div>
            <div className="user-email">{user.email}</div>
            <div className="user-meta">
              <span className={`role-badge ${getRoleClass(user.role)}`}>{user.role}</span>
              <span className={`status-badge ${getStatusClass(user.status)}`}>{user.status}</span>
            </div>
          </div>
        </div>
        <div className="user-details">
          <div className="user-clinics">
            <div className="user-clinics-label">CLINIC ACCESS ({user.clinics.length})</div>
            {user.clinics.map((clinic, index) => (
              <div key={index} className="user-clinic-item">
                <span>{clinic.name}</span>
              </div>
            ))}
          </div>
          {userPractitioners.length > 0 && (
            <div className="user-practitioners">
              <div className="user-practitioners-label">MANAGES PRACTITIONERS ({userPractitioners.length})</div>
              {userPractitioners.slice(0, 3).map((practitioner, index) => (
                <div key={index} className="user-practitioner-item">
                  <span>{practitioner.name}</span>
                  <span className="practitioner-role">{practitioner.association.role}</span>
                </div>
              ))}
              {userPractitioners.length > 3 && (
                <div className="user-practitioner-item">
                  <span>+{userPractitioners.length - 3} more...</span>
                </div>
              )}
            </div>
          )}
          <div className="user-last-active">Last active: {user.lastActive}</div>
        </div>
        <div className="user-actions">
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => handleViewUser(user.id)}
          >
            View
          </button>
          <button 
            className="btn btn-secondary btn-sm" 
            onClick={() => handleEditUser(user.id)}
          >
            Edit
          </button>
          {(user.role === 'Practice Manager' || user.role === 'Clinic Coordinator' || user.role === 'Receptionist' || user.role === 'Admin Assistant') && (
            <button 
              className="btn btn-primary btn-sm" 
              onClick={() => handleManagePractitioners(user.id)}
            >
              Manage Practitioners
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="user-management-view">
      {/* Page Header */}
      <PageHeader pageKey="users">
        <button
          className="btn btn-secondary"
          disabled={selectedUsers.length === 0}
        >
          <span className="icon icon--clipboard icon--default"></span>
          Bulk Actions ({selectedUsers.length})
        </button>
        <button className="btn btn-primary" onClick={handleAddUser}>
          <span className="icon icon--add icon--success"></span>
          Add User
        </button>
      </PageHeader>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Users</h3>
        </div>
        
        <div className="filters-bar">
          <div className="filter-group">
            <input
              type="text"
              className="search-input"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select 
              className="filter-select" 
              value={filterRole} 
              onChange={(e) => setFilterRole(e.target.value)}
            >
              {roles.map(role => (
                <option key={role} value={role}>
                  {role === 'all' ? 'All Roles' : role}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <select 
              className="filter-select" 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              {statuses.map(status => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Status' : status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="card-content">
          {loading ? (
            <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
              <div className="loading-spinner" style={{ display: 'inline-block' }}></div>
              <p>Loading users...</p>
            </div>
          ) : error ? (
            <div className="error-state" style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
              <p>{error}</p>
              <button onClick={() => fetchUsers()} className="btn btn-secondary btn-sm">
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="results-info">
                Showing {filteredUsers.length} of {users.length} users
                {pagination.totalCount > 0 && (
                  <span> (Total: {pagination.totalCount})</span>
                )}
              </div>

              <div className="user-grid">
                {filteredUsers.length > 0 ? (
                  filteredUsers.map(renderUserCard)
                ) : (
                  <div className="no-users">
                    <p>No users found.</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// User Form Component
const UserForm = ({ user = null, onSubmit, onCancel, availableClinics, rolePermissions, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'Receptionist',
    phone: user?.phone || '',
    clinics: user?.clinics?.map(c => c.id) || [],
    status: user?.status || 'active',
    sendWelcome: !isEdit,
    calendarIntegration: user?.calendarIntegration || {
      googleCalendar: { enabled: false, email: '' },
      outlookCalendar: { enabled: false, email: '' },
      timezone: 'Europe/London',
      syncBookings: false
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role) {
      alert('Please fill in all required fields');
      return;
    }
    if (formData.clinics.length === 0) {
      alert('Please select at least one clinic');
      return;
    }
    onSubmit(formData);
  };

  const handleCalendarChange = (provider, field, value) => {
    setFormData(prev => ({
      ...prev,
      calendarIntegration: {
        ...prev.calendarIntegration,
        [provider]: {
          ...prev.calendarIntegration[provider],
          [field]: value
        }
      }
    }));
  };

  const selectedRole = rolePermissions[formData.role];

  return (
    <form onSubmit={handleSubmit} className="user-form">
      <div className="form-sections">
        {/* Basic Information */}
        <div className="form-section">
          <h4>Basic Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter full name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="Enter phone number"
              />
            </div>
            {isEdit && (
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Role & Permissions */}
        <div className="form-section">
          <h4>Role & Access</h4>
          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              required
            >
              {Object.keys(rolePermissions).map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          
          {selectedRole && (
            <div className="role-description">
              <p className="role-info">{selectedRole.description}</p>
              <div className="permission-preview">
                <strong>Default Permissions:</strong>
                <div className="permission-list">
                  {selectedRole.defaultPermissions.map(permission => (
                    <span key={permission} className="permission-badge">{permission}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Clinic Access */}
        <div className="form-section">
          <h4>Clinic Access *</h4>
          <div className="clinic-checkboxes">
            {availableClinics.map(clinic => (
              <label key={clinic.id} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.clinics.includes(clinic.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({
                        ...formData,
                        clinics: [...formData.clinics, clinic.id]
                      });
                    } else {
                      setFormData({
                        ...formData,
                        clinics: formData.clinics.filter(id => id !== clinic.id)
                      });
                    }
                  }}
                />
                <span className="checkmark"></span>
                {clinic.name}
              </label>
            ))}
          </div>
        </div>

        {/* Calendar Integration */}
        <div className="form-section">
          <h4>Calendar Integration</h4>
          <div className="calendar-integration">
            <div className="calendar-provider">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.calendarIntegration.googleCalendar.enabled}
                  onChange={(e) => handleCalendarChange('googleCalendar', 'enabled', e.target.checked)}
                />
                <span className="checkmark"></span>
                Google Calendar
              </label>
              {formData.calendarIntegration.googleCalendar.enabled && (
                <input
                  type="email"
                  placeholder="Google Calendar email"
                  value={formData.calendarIntegration.googleCalendar.email}
                  onChange={(e) => handleCalendarChange('googleCalendar', 'email', e.target.value)}
                  className="calendar-email"
                />
              )}
            </div>
            
            <div className="calendar-provider">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.calendarIntegration.outlookCalendar.enabled}
                  onChange={(e) => handleCalendarChange('outlookCalendar', 'enabled', e.target.checked)}
                />
                <span className="checkmark"></span>
                Outlook Calendar
              </label>
              {formData.calendarIntegration.outlookCalendar.enabled && (
                <input
                  type="email"
                  placeholder="Outlook Calendar email"
                  value={formData.calendarIntegration.outlookCalendar.email}
                  onChange={(e) => handleCalendarChange('outlookCalendar', 'email', e.target.value)}
                  className="calendar-email"
                />
              )}
            </div>

            <div className="form-group">
              <label htmlFor="timezone">Timezone</label>
              <select
                id="timezone"
                value={formData.calendarIntegration.timezone}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  calendarIntegration: { ...prev.calendarIntegration, timezone: e.target.value }
                }))}
              >
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="America/New_York">New York (EST)</option>
                <option value="America/Los_Angeles">Los Angeles (PST)</option>
              </select>
            </div>

            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.calendarIntegration.syncBookings}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  calendarIntegration: { ...prev.calendarIntegration, syncBookings: e.target.checked }
                }))}
              />
              <span className="checkmark"></span>
              Automatically sync bookings to calendar
            </label>
          </div>
        </div>

        {/* Additional Options */}
        {!isEdit && (
          <div className="form-section">
            <h4>Additional Options</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.sendWelcome}
                onChange={(e) => setFormData({ ...formData, sendWelcome: e.target.checked })}
              />
              <span className="checkmark"></span>
              Send welcome email with login instructions
            </label>
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {isEdit ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

// User Detail Component
const UserDetail = ({ user, practitioners, onEdit, onManagePractitioners }) => {
  return (
    <div className="user-detail">
      <div className="detail-sections">
        {/* User Header */}
        <div className="detail-header">
          <div className="user-avatar-lg">{user.avatar}</div>
          <div className="user-header-info">
            <h3>{user.name}</h3>
            <p className="user-email">{user.email}</p>
            <div className="user-badges">
              <span className={`role-badge ${user.role.replace(' ', '-').toLowerCase()}`}>
                {user.role}
              </span>
              <span className={`status-badge status-${user.status}`}>
                {user.status}
              </span>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="detail-section">
          <h4>Contact Information</h4>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Phone</label>
              <span>{user.phone || 'Not provided'}</span>
            </div>
            <div className="detail-item">
              <label>Department</label>
              <span>{user.department}</span>
            </div>
            <div className="detail-item">
              <label>Join Date</label>
              <span>{new Date(user.joinDate).toLocaleDateString('en-GB')}</span>
            </div>
            <div className="detail-item">
              <label>Last Active</label>
              <span>{user.lastActive}</span>
            </div>
          </div>
        </div>

        {/* Clinic Access */}
        <div className="detail-section">
          <h4>Clinic Access ({user.clinics.length})</h4>
          <div className="clinic-list">
            {user.clinics.map((clinic, index) => (
              <div key={index} className="clinic-item">
                <span className="clinic-name">{clinic.name}</span>
                <span className="clinic-role">{clinic.role}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Practitioner Associations */}
        {practitioners.length > 0 && (
          <div className="detail-section">
            <h4>Associated Practitioners ({practitioners.length})</h4>
            <div className="practitioner-associations">
              {practitioners.map((practitioner, index) => (
                <div key={index} className="practitioner-item">
                  <div className="practitioner-info">
                    <span className="practitioner-name">{practitioner.name}</span>
                    <span className="practitioner-specialization">{practitioner.specialization}</span>
                  </div>
                  <div className="practitioner-role">
                    <span className={`role-badge role-${practitioner.association.role}`}>
                      {practitioner.association.role}
                    </span>
                  </div>
                  <div className="practitioner-permissions">
                    {practitioner.association.permissions.map(permission => (
                      <span key={permission} className="permission-tag">{permission}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Permissions */}
        <div className="detail-section">
          <h4>System Permissions</h4>
          <div className="permission-list">
            {user.permissions.map(permission => (
              <span key={permission} className="permission-badge">{permission}</span>
            ))}
          </div>
        </div>

        {/* Calendar Integration */}
        {user.calendarIntegration && (
          <div className="detail-section">
            <h4>Calendar Integration</h4>
            <div className="calendar-info">
              <div className="calendar-provider-info">
                <div className="provider-status">
                  <span className={`status-indicator ${user.calendarIntegration.googleCalendar.enabled ? 'enabled' : 'disabled'}`}></span>
                  <strong>Google Calendar:</strong> 
                  {user.calendarIntegration.googleCalendar.enabled ? 
                    ` Connected (${user.calendarIntegration.googleCalendar.email})` : 
                    ' Not connected'
                  }
                </div>
                <div className="provider-status">
                  <span className={`status-indicator ${user.calendarIntegration.outlookCalendar.enabled ? 'enabled' : 'disabled'}`}></span>
                  <strong>Outlook Calendar:</strong> 
                  {user.calendarIntegration.outlookCalendar.enabled ? 
                    ` Connected (${user.calendarIntegration.outlookCalendar.email})` : 
                    ' Not connected'
                  }
                </div>
              </div>
              <div className="calendar-settings">
                <div className="setting-item">
                  <label>Timezone:</label>
                  <span>{user.calendarIntegration.timezone}</span>
                </div>
                <div className="setting-item">
                  <label>Auto-sync bookings:</label>
                  <span>{user.calendarIntegration.syncBookings ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="detail-actions">
        <button onClick={onEdit} className="btn btn-secondary">
          Edit User
        </button>
        {(user.role === 'Practice Manager' || user.role === 'Clinic Coordinator' || user.role === 'Receptionist' || user.role === 'Admin Assistant') && (
          <button onClick={onManagePractitioners} className="btn btn-primary">
            Manage Practitioners
          </button>
        )}
      </div>
    </div>
  );
};

// Assign Practitioners Form Component
const AssignPractitionersForm = ({ 
  user, 
  practitioners, 
  currentAssociations, 
  availableClinics, 
  rolePermissions, 
  onSubmit, 
  onCancel 
}) => {
  const [associations, setAssociations] = useState(
    currentAssociations.map(assoc => ({
      practitionerId: assoc.practitionerId,
      role: assoc.role,
      permissions: assoc.permissions,
      clinicId: assoc.clinicId
    }))
  );

  const userRolePermissions = rolePermissions[user.role]?.practitionerPermissions || [];
  const availablePermissions = [
    { id: 'view', label: 'View Schedule', description: 'Can view practitioner schedules and appointments' },
    { id: 'book', label: 'Book Appointments', description: 'Can create new appointments' },
    { id: 'edit', label: 'Edit Appointments', description: 'Can modify existing appointments' },
    { id: 'cancel', label: 'Cancel Appointments', description: 'Can cancel appointments' },
    { id: 'reschedule', label: 'Reschedule', description: 'Can reschedule appointments' },
    { id: 'manage-schedule', label: 'Manage Schedule', description: 'Can manage practitioner availability' },
    { id: 'view-own', label: 'View Own Only', description: 'Can only view own schedule (for practitioners)' }
  ];

  const handleAddPractitioner = () => {
    const unassignedPractitioners = practitioners.filter(p => 
      !associations.some(a => a.practitionerId === p.id) && p.status === 'active'
    );
    
    if (unassignedPractitioners.length > 0) {
      setAssociations([
        ...associations,
        {
          practitionerId: unassignedPractitioners[0].id,
          role: 'assistant',
          permissions: ['view'],
          clinicId: user.clinics[0]?.id || ''
        }
      ]);
    }
  };

  const handleRemovePractitioner = (index) => {
    setAssociations(associations.filter((_, i) => i !== index));
  };

  const handleAssociationChange = (index, field, value) => {
    const updatedAssociations = associations.map((assoc, i) => {
      if (i === index) {
        if (field === 'role') {
          // Reset permissions when role changes
          const defaultPermissions = getDefaultPermissionsForRole(value, userRolePermissions);
          return { ...assoc, [field]: value, permissions: defaultPermissions };
        }
        return { ...assoc, [field]: value };
      }
      return assoc;
    });
    setAssociations(updatedAssociations);
  };

  const handlePermissionToggle = (index, permission) => {
    const updatedAssociations = associations.map((assoc, i) => {
      if (i === index) {
        const hasPermission = assoc.permissions.includes(permission);
        const updatedPermissions = hasPermission
          ? assoc.permissions.filter(p => p !== permission)
          : [...assoc.permissions, permission];
        return { ...assoc, permissions: updatedPermissions };
      }
      return assoc;
    });
    setAssociations(updatedAssociations);
  };

  const getDefaultPermissionsForRole = (role, userPermissions) => {
    const roleDefaults = {
      'manager': ['view', 'edit', 'book', 'cancel', 'reschedule'],
      'assistant': ['view', 'book', 'cancel'],
      'viewer': ['view']
    };
    
    const defaults = roleDefaults[role] || ['view'];
    // Only include permissions that the user's role allows
    return defaults.filter(permission => userPermissions.includes(permission));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate associations
    for (let i = 0; i < associations.length; i++) {
      const assoc = associations[i];
      if (!assoc.practitionerId || !assoc.clinicId || assoc.permissions.length === 0) {
        alert(`Please complete all fields for association ${i + 1}`);
        return;
      }
    }
    
    onSubmit(associations);
  };

  const getPractitionerName = (practitionerId) => {
    const practitioner = practitioners.find(p => p.id === practitionerId);
    return practitioner ? practitioner.name : 'Unknown Practitioner';
  };

  const getClinicName = (clinicId) => {
    const clinic = availableClinics.find(c => c.id === clinicId);
    return clinic ? clinic.name : 'Unknown Clinic';
  };

  const unassignedPractitioners = practitioners.filter(p => 
    !associations.some(a => a.practitionerId === p.id) && p.status === 'active'
  );

  return (
    <form onSubmit={handleSubmit} className="assign-practitioners-form">
      <div className="form-header">
        <div className="user-context">
          <div className="user-avatar-sm">{user.avatar}</div>
          <div>
            <h4>{user.name}</h4>
            <p>{user.role} • {user.clinics.map(c => c.name).join(', ')}</p>
          </div>
        </div>
        <div className="role-permissions-info">
          <strong>Available permissions for {user.role}:</strong>
          <div className="permission-list">
            {userRolePermissions.map(permission => (
              <span key={permission} className="permission-badge">{permission}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="associations-section">
        <div className="section-header">
          <h4>Practitioner Associations ({associations.length})</h4>
          <button
            type="button"
            onClick={handleAddPractitioner}
            className="btn btn-secondary btn-sm"
            disabled={unassignedPractitioners.length === 0}
          >
            + Add Practitioner
          </button>
        </div>

        {associations.length === 0 ? (
          <div className="no-associations">
            <p>No practitioners assigned. Click "Add Practitioner" to get started.</p>
          </div>
        ) : (
          <div className="associations-list">
            {associations.map((association, index) => (
              <div key={index} className="association-item">
                <div className="association-header">
                  <h5>Association #{index + 1}</h5>
                  <button
                    type="button"
                    onClick={() => handleRemovePractitioner(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Remove
                  </button>
                </div>

                <div className="association-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Practitioner</label>
                      <select
                        value={association.practitionerId}
                        onChange={(e) => handleAssociationChange(index, 'practitionerId', e.target.value)}
                        required
                      >
                        <option value="">Select Practitioner</option>
                        {practitioners
                          .filter(p => p.status === 'active')
                          .filter(p => p.id === association.practitionerId || !associations.some(a => a.practitionerId === p.id))
                          .map(practitioner => (
                            <option key={practitioner.id} value={practitioner.id}>
                              {practitioner.name} - {practitioner.specialization}
                            </option>
                          ))
                        }
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Clinic</label>
                      <select
                        value={association.clinicId}
                        onChange={(e) => handleAssociationChange(index, 'clinicId', e.target.value)}
                        required
                      >
                        <option value="">Select Clinic</option>
                        {user.clinics.map(clinic => (
                          <option key={clinic.id} value={clinic.id}>
                            {clinic.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Role</label>
                      <select
                        value={association.role}
                        onChange={(e) => handleAssociationChange(index, 'role', e.target.value)}
                        required
                      >
                        <option value="manager">Manager</option>
                        <option value="assistant">Assistant</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    </div>
                  </div>

                  <div className="permissions-section">
                    <label>Permissions</label>
                    <div className="permissions-grid">
                      {availablePermissions
                        .filter(perm => userRolePermissions.includes(perm.id))
                        .map(permission => (
                          <label key={permission.id} className="permission-checkbox">
                            <input
                              type="checkbox"
                              checked={association.permissions.includes(permission.id)}
                              onChange={() => handlePermissionToggle(index, permission.id)}
                            />
                            <span className="checkmark"></span>
                            <div className="permission-info">
                              <span className="permission-label">{permission.label}</span>
                              <span className="permission-description">{permission.description}</span>
                            </div>
                          </label>
                        ))
                      }
                    </div>
                  </div>

                  {association.practitionerId && association.clinicId && (
                    <div className="association-summary">
                      <strong>Summary:</strong> {getPractitionerName(association.practitionerId)} at {getClinicName(association.clinicId)} 
                      as {association.role} with {association.permissions.length} permission(s)
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={associations.length === 0}>
          Save Associations ({associations.length})
        </button>
      </div>
    </form>
  );
};

export default UserManagementView;